<!-- Team Management Page -->
<div class="team-page">
  <!-- Page Header -->
  <div class="page-header">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h1 class="page-title">
          <i class="bi bi-people-fill me-2"></i>
          Team
        </h1>
        <p class="page-subtitle text-muted mb-0">
          Manage your organization's team members and invitations
        </p>
      </div>
      <div *ngIf="canInviteMembers()">
        <button
          type="button"
          class="btn btn-primary"
          (click)="openInviteModal()"
          [disabled]="isLoading()"
        >
          <i class="bi bi-person-plus-fill me-2"></i>
          Invite Member
        </button>
      </div>
    </div>
  </div>

  <!-- Error Alert -->
  <div *ngIf="error()" class="alert alert-danger alert-dismissible fade show" role="alert">
    <i class="bi bi-exclamation-triangle-fill me-2"></i>
    {{ error() }}
    <button type="button" class="btn-close" (click)="teamService.clearError()" aria-label="Close"></button>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading() && !invitations().length" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="text-muted mt-3">Loading invitations...</p>
  </div>

  <!-- Pending Invitations Section (visible only to Owner and Chief Supervisor) -->
  <div *ngIf="canInviteMembers() && (!isLoading() || invitations().length)" class="card p-3 my-5">
    <div class="card-header">
      <h3 class="card-title bold">
        <strong>Pending Invitations</strong>
        <span class="badge bg-secondary ms-2">{{ pendingInvitations().length }}</span>
      </h3>
      <p class="small text-muted">
        Team members who have been invited but haven't accepted yet
      </p>
    </div>

    <!-- Empty State -->
    <div *ngIf="!pendingInvitations().length" class="text-center py-5">
      <i class="bi bi-envelope-open display-4 text-muted opacity-50 d-block mb-3"></i>
      <h3><strong>No Pending Invitations</strong></h3>
      <p class="mx-auto mw-32rem">
        There are currently no pending invitations. Invite team members to get started.
      </p>
        <button
          *ngIf="canInviteMembers()"
          type="button"
          class="btn btn-primary"
          (click)="openInviteModal()"
        >
          <i class="bi bi-person-plus-fill me-2"></i>
          Invite Your First Member
        </button>
    </div>

    <!-- Invitations Table -->
    <div *ngIf="pendingInvitations().length" class="table-responsive">
      <table class="table table-hover">
        <thead>
          <tr>
            <th>Email</th>
            <th>Role(s)</th>
            <th>Invited By</th>
            <th>Expires</th>
            <th>Resends</th>
            <th *ngIf="canInviteMembers()" class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let invitation of pendingInvitations()">
            <!-- Email -->
            <td>
              <span class="text-muted">{{ invitation.email }}</span>
            </td>

            <!-- Roles -->
            <td>
              <span
                *ngFor="let role of getRoleNames(invitation.role)"
                [class]="getRoleBadgeClass(role)"
                class="me-1"
              >
                {{ role }}
              </span>
            </td>

            <!-- Invited By -->
            <td>
              <span *ngIf="invitation.inviterName">
                {{ invitation.inviterName }}
              </span>
              <span *ngIf="!invitation.inviterName" class="text-muted">-</span>
            </td>

            <!-- Expiration -->
            <td>
              <span [class]="getExpirationClass(invitation.expiresAt)">
                <i class="bi bi-clock me-1"></i>
                {{ getExpirationDisplay(invitation.expiresAt) }}
              </span>
            </td>

            <!-- Resend Count -->
            <td>
              <span *ngIf="invitation.resendCount > 0" class="badge bg-info">
                <i class="bi bi-arrow-repeat me-1"></i>
                {{ invitation.resendCount }}
              </span>
              <span *ngIf="invitation.resendCount === 0" class="text-muted">-</span>
            </td>

            <!-- Actions -->
            <td *ngIf="canInviteMembers()" class="text-end">
              <div class="btn-group" role="group">
                <!-- Resend Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  [disabled]="!canResend(invitation) || isProcessing(invitation.id)"
                  [title]="canResend(invitation) ? 'Resend invitation' : getResendDisabledReason(invitation)"
                  (click)="resendInvitation(invitation)"
                >
                  <span *ngIf="isProcessing(invitation.id)" class="spinner-border spinner-border-sm me-1"></span>
                  <i *ngIf="!isProcessing(invitation.id)" class="bi bi-arrow-repeat"></i>
                  Resend
                </button>

                <!-- Copy Link Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  [disabled]="isProcessing(invitation.id)"
                  title="Copy invitation link"
                  (click)="copyInvitationLink(invitation)"
                >
                  <i class="bi bi-link-45deg"></i>
                </button>

                <!-- Cancel Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  [disabled]="isProcessing(invitation.id)"
                  title="Cancel invitation"
                  (click)="cancelInvitation(invitation)"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Team Members Section -->
  <div class="card p-3 my-5">
    <div class="card-header">
      <h3 class="card-title bold">
        <strong>Team Members</strong>
        <span class="badge bg-primary ms-2">{{ activeMembers().length }}</span>
      </h3>
      <p class="small text-muted">
        Active team members in your organization
      </p>
    </div>

    <!-- Empty State -->
    <div *ngIf="!activeMembers().length && !isLoading()" class="text-center py-5">
      <i class="bi bi-people display-4 text-muted opacity-50 d-block mb-3"></i>
      <h3><strong>No Team Members Yet</strong></h3>
      <p class="mx-auto mw-32rem">
        Your organization doesn't have any members yet.
      </p>
    </div>

    <!-- Members Table -->
    <div *ngIf="activeMembers().length" class="table-responsive">
      <table class="table table-hover">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role(s)</th>
            <th>Member Since</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let member of activeMembers()">
            <!-- Name -->
            <td>
              <strong>{{ member.firstName }} {{ member.lastName }}</strong>
            </td>

            <!-- Email -->
            <td>
              <span class="text-muted">{{ member.email }}</span>
            </td>

            <!-- Roles -->
            <td>
              <div class="d-flex flex-wrap gap-1">
                <span
                  *ngFor="let roleName of getRoleNames(member.combinedRole ?? member.role)"
                  class="badge"
                  [ngClass]="getRoleBadgeClass(roleName)"
                >
                  {{ roleName }}
                </span>
              </div>
            </td>

            <!-- Member Since -->
            <td>
              <span class="text-muted">{{ member.createdAt | date: 'MMM d, yyyy' }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>

<!-- Invite Member Modal -->
<app-invite-member-modal
  #inviteModal
  (invitationCreated)="onInvitationCreated()"
></app-invite-member-modal>

<!-- Confirm Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onCancelConfirmed()"
  (cancelled)="onCancelCancelled()"
></app-confirm-modal>

<!-- Toast Notification -->
<div
  *ngIf="toastMessage()"
  class="toast-container position-fixed top-0 end-0 p-3"
>
  <div
    class="toast show"
    [class.bg-success]="toastType() === 'success'"
    [class.bg-danger]="toastType() === 'error'"
    [class.bg-info]="toastType() === 'info'"
    role="alert"
  >
    <div class="toast-header">
      <i
        class="bi me-2"
        [class.bi-check-circle-fill]="toastType() === 'success'"
        [class.bi-exclamation-triangle-fill]="toastType() === 'error'"
        [class.bi-info-circle-fill]="toastType() === 'info'"
      ></i>
      <strong class="me-auto">
        {{ toastType() === 'success' ? 'Success' : toastType() === 'error' ? 'Error' : 'Info' }}
      </strong>
      <button type="button" class="btn-close" (click)="closeToast()" aria-label="Close"></button>
    </div>
    <div class="toast-body text-white">
      {{ toastMessage() }}
    </div>
  </div>
</div>
